import type {
  AttendanceEntitiesCompensationBlockCompensationTypeEnum,
  AttendanceEnumsEmployeeTypeEnum,
  AttendanceEnumsPoolTypeEnum,
} from 'generated/resources/graphql'

import CompensationBlockUtils from 'modules/attendance/lib/utils/CompensationBlockUtils'

export enum AlertType {
  missingClockIn = 'missing_clock_in',
  missingClockOut = 'missing_clock_out',
  missingBreakTime = 'missing_break_time',
  insufficientBreakDuration = 'insufficient_break_duration',
}

export enum PopoverTypeToShow {
  Info = 'info',
  Count = 'count',
}

export const PeriodState = {
  pending: 'pending',
  approved: 'approved',
  in_progress: 'in_progress',
}

export interface NavigationParams {
  year: string
  month: string
  day: string
  periodType: string
  employeeId?: string
}

export const EMPLOYEE_TYPE: Record<'FULL_TIME' | 'PART_TIME', AttendanceEnumsEmployeeTypeEnum> = {
  FULL_TIME: 'full_time',
  PART_TIME: 'part_time',
} as const

export const COMPENSATION_POOL_TYPE: Record<
  'TIMEOFF' | 'VIRTUAL_PAYROLL' | 'BALANCE',
  AttendanceEnumsPoolTypeEnum
> = {
  TIMEOFF: 'timeoff',
  VIRTUAL_PAYROLL: 'virtual_payroll',
  BALANCE: 'balance',
} as const

// Types for Amplitude events properties

export const AmplitudeProps = {
  Origin: {
    ComplementaryHours: 'complementary_hours',
    ExtraHours: 'extra_hours',
  },
  ComplementaryHoursStatus: {
    enabled: 'enabled',
    disabled: 'disabled',
  },
} as const

/** Manual allocation: minutes to compensate per block and compensation type (payroll | timeoff) */
export interface CompensationBlockAllocationInput {
  selectionKey: string
  minutesToCompensate?: number
  compensationType?: 'payroll' | 'timeoff'
}

/** State for the single BOH block allocation (time off vs payroll split) */
export interface BOHAllocationState {
  timeOffMinutes: number
  payrollMinutes: number
}

/**
 * Builds the compensation_blocks payload for the mutation.
 * Always sends compensationBlocks (never compensation_block_ids).
 * - BOH blocks with manual allocation: split into payroll/timeoff entries with minutesToCompensate
 * - Non-partial blocks: only selectionKey (backend compensates full amount)
 */
export function buildAllCompensationBlocksPayload(
  selectedBlocks: Array<{
    id: string
    compensationType?: AttendanceEntitiesCompensationBlockCompensationTypeEnum
  }>,
  bohAllocation: BOHAllocationState | null
): CompensationBlockAllocationInput[] {
  const blocks: CompensationBlockAllocationInput[] = []
  for (const block of selectedBlocks) {
    const selectionKey = block.id
    if (CompensationBlockUtils.isExtraHoursFromBankOfHours(block) && bohAllocation) {
      if (bohAllocation.timeOffMinutes > 0) {
        blocks.push({
          selectionKey,
          minutesToCompensate: bohAllocation.timeOffMinutes,
          compensationType: 'timeoff',
        })
      }
      if (bohAllocation.payrollMinutes > 0) {
        blocks.push({
          selectionKey,
          minutesToCompensate: bohAllocation.payrollMinutes,
          compensationType: 'payroll',
        })
      }
    } else {
      blocks.push({ selectionKey })
    }
  }
  return blocks
}
